
import React, { useEffect } from 'react';
import { AsteroidsGameEngine } from '../AsteroidsGameEngine';
import { throttle } from '../utils';

interface GameCanvasProps {
  canvasRef: React.RefObject<HTMLCanvasElement>;
  containerRef: React.RefObject<HTMLDivElement>;
  gameEngine: AsteroidsGameEngine | null;
  updateCanvasSize: () => void;
}

const GameCanvas: React.FC<GameCanvasProps> = ({ 
  canvasRef, 
  containerRef,
  gameEngine,
  updateCanvasSize
}) => {
  // Set up keyboard controls
  useEffect(() => {
    if (!gameEngine) return;
    
    // Throttle keyboard inputs to prevent excessive state updates
    const handleKeyDown = throttle((e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') gameEngine.rotateShipLeft();
      if (e.key === 'ArrowRight') gameEngine.rotateShipRight();
      if (e.key === 'ArrowUp') gameEngine.startMoving();
      if (e.key === ' ') gameEngine.fireBullet();
    }, 16); // ~60fps
    
    const handleKeyUp = throttle((e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') gameEngine.stopRotation();
      if (e.key === 'ArrowUp') gameEngine.stopMoving();
    }, 16);
    
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    
    // Add resize listener
    window.addEventListener('resize', updateCanvasSize);
    
    return () => {
      // Cleanup
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, [gameEngine, updateCanvasSize]);
  
  return (
    <div 
      ref={containerRef} 
      className="w-full h-full flex items-center justify-center bg-black relative"
    >
      <canvas ref={canvasRef} className="max-w-full max-h-full bg-black" />
    </div>
  );
};

export default React.memo(GameCanvas);
